import { Button, Dialog, DropdownButton, LoadingSpinner, Menu, Switch, Tabs, Typography } from '@neo4j-ndl/react';
import {
  BuildingOfficeIconOutline,
  CheckIconOutline,
  GlobeAltIconOutline,
  UserIconOutline,
} from '@neo4j-ndl/react/icons';
import { type ReactNode, useRef, useState } from 'react';

export type SharingDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  description?: string;
  children: React.ReactNode;
};

export const SharingDialog = ({ isOpen, onClose, description, children }: SharingDialogProps) => {
  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <Dialog.Header>Share</Dialog.Header>
      <Dialog.Description>{description}</Dialog.Description>
      <Dialog.Content className="flex flex-col">{children}</Dialog.Content>
    </Dialog>
  );
};

const SharingDialogSubHeader = ({ children }: { children: React.ReactNode }) => {
  return (
    <Typography variant="h6" className="mb-4 mt-6">
      {children}
    </Typography>
  );
};

type SelectOption<T> = {
  label: string;
  value: T;
};

type SharingRowType<T> =
  | {
      type: 'static';
      label: string;
    }
  | {
      type: 'select';
      options: (SelectOption<T> | 'divider')[];
      selectedOption: SelectOption<T>;
      onChange: (option: SelectOption<T>) => void;
      loading: boolean;
    }
  | {
      type: 'switch';
      label: string;
      value: boolean;
      onChange: (value: boolean) => void;
      loading: boolean;
    };

type SharingDialogRowProps<T> = {
  name: string;
  description: string;
  sharingType: SharingRowType<T>;
  entityType: 'person' | 'organization' | 'global';
};

const WithLeftLoadingSpinner = ({ children, loading }: { children: React.ReactNode; loading: boolean }) => (
  <div className="flex flex-row items-center gap-4">
    {loading && <LoadingSpinner size="small" />}
    {children}
  </div>
);

const SharingDialogRightContent = <T,>({ sharingType }: { sharingType: SharingRowType<T> }) => {
  const selectTypeAnchorRef = useRef<HTMLButtonElement>(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  return (
    <>
      {sharingType.type === 'static' && (
        <Typography variant="body-medium" className="text-palette-neutral-text-weak">
          {sharingType.label}
        </Typography>
      )}
      {sharingType.type === 'select' && (
        <WithLeftLoadingSpinner loading={sharingType.loading}>
          <DropdownButton
            ref={selectTypeAnchorRef}
            htmlAttributes={{ onClick: () => setIsSelectOpen((isOpen) => !isOpen) }}
            isOpen={isSelectOpen}
            size="medium"
          >
            {sharingType.selectedOption.label}
          </DropdownButton>
          <Menu
            anchorRef={selectTypeAnchorRef}
            isOpen={isSelectOpen}
            onClose={() => setIsSelectOpen(false)}
            placement="bottom-end-top-end"
            isPortaled={false}
          >
            <Menu.Items>
              <>
                {sharingType.options.map((option, i) => {
                  return option === 'divider' ? (
                    <Menu.Divider key={`divider-${i}`} />
                  ) : (
                    <Menu.Item
                      key={option.label}
                      icon={sharingType.selectedOption.value === option.value ? <CheckIconOutline /> : undefined}
                      title={option.label}
                      onClick={() => sharingType.onChange(option)}
                    />
                  );
                })}
              </>
            </Menu.Items>
          </Menu>
        </WithLeftLoadingSpinner>
      )}
      {sharingType.type === 'switch' && (
        <WithLeftLoadingSpinner loading={sharingType.loading}>
          <Switch
            label={sharingType.label}
            hasLabelBefore
            isChecked={sharingType.value}
            onChange={(newValue) => sharingType.onChange(newValue.target.checked)}
          />
        </WithLeftLoadingSpinner>
      )}
    </>
  );
};

const SharingDialogRow = <T,>({ name, description, sharingType, entityType }: SharingDialogRowProps<T>) => {
  return (
    <div className="hover:bg-neutral-hover flex flex-row items-center rounded-sm p-2">
      <div className="mr-3 flex flex-row items-center gap-3">
        {entityType === 'person' && <UserIconOutline className="text-palette-neutral-icon size-5" />}
        {entityType === 'organization' && <BuildingOfficeIconOutline className="text-palette-neutral-icon size-5" />}
        {entityType === 'global' && <GlobeAltIconOutline className="text-palette-neutral-icon size-5" />}
      </div>
      <div className="flex flex-col">
        <Typography variant="body-medium">{name}</Typography>
        <Typography variant="body-medium" className="text-palette-neutral-text-weak">
          {description}
        </Typography>
      </div>
      <div className="ml-auto flex flex-col">
        <SharingDialogRightContent sharingType={sharingType} />
      </div>
    </div>
  );
};

const SharingDialogFooter = ({ children, onDone }: { children: React.ReactNode; onDone: () => void }) => {
  return (
    <Dialog.Actions>
      <div className="mr-auto flex flex-row items-center">{children}</div>
      <Button type="button" fill="outlined" color="neutral" onClick={onDone}>
        Done
      </Button>
    </Dialog.Actions>
  );
};

type Tab = {
  id: string;
  label: string;
  content: ReactNode;
  tabClassName?: string;
};

type SharingDialogTabsProps = {
  tabs: Tab[];
  value: string;
  onChange: (value: string) => void;
};

const SharingDialogTabs = ({ tabs, value, onChange }: SharingDialogTabsProps) => {
  return (
    <Tabs fill="filled" size="large" className="" value={value} onChange={onChange}>
      {tabs.map((tab) => (
        <Tabs.Tab key={tab.id} tabId={tab.id} className={tab.tabClassName}>
          <Typography variant="subheading-small">{tab.label}</Typography>
        </Tabs.Tab>
      ))}
      {tabs.map((tab) => (
        <Tabs.TabPanel key={tab.id} tabId={tab.id} value={value}>
          {tab.content}
        </Tabs.TabPanel>
      ))}
    </Tabs>
  );
};

SharingDialog.SubHeader = SharingDialogSubHeader;
SharingDialog.Row = SharingDialogRow;
SharingDialog.Footer = SharingDialogFooter;
SharingDialog.Tabs = SharingDialogTabs;
